import { Heart, Send } from 'lucide-react'
import {
  getOutputPreset,
  OutputPresetId,
  SafeZoneId,
  type SafeZone,
} from '@/content/instagram'
import { TEST_IDS } from '@/lib/testIds'
import { PreviewCanvas } from '@/tools/preview/PreviewCanvas'
import type { PreviewTile } from '@/tools/preview/renderPreviewTiles'

interface StoryPreviewProps {
  tiles: readonly PreviewTile[]
}

const STORY_PRESET = getOutputPreset(OutputPresetId.Story)
function getRequiredStorySafeZone(id: SafeZoneId): SafeZone {
  const zone = STORY_PRESET.safeZones?.find((candidate) => candidate.id === id)
  if (!zone) throw new Error(`Story preview requires configured safe zone "${id}"`)
  return zone
}

const STORY_TOP_ZONE = getRequiredStorySafeZone(SafeZoneId.StoryTop)
const STORY_BOTTOM_ZONE = getRequiredStorySafeZone(SafeZoneId.StoryBottom)

export function StoryPreview({ tiles }: StoryPreviewProps) {
  if (tiles.length !== 1 || tiles[0]?.slides.length !== 1) {
    throw new Error('Story preview requires exactly one rendered image')
  }
  const source = tiles[0].postCanvas

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto bg-muted/30 p-3" data-testid={TEST_IDS.previewStory}>
      <div className="relative aspect-[9/16] h-full max-h-[680px] max-w-full overflow-hidden rounded-[1.75rem] bg-black shadow-xl">
        <PreviewCanvas
          source={source}
          sourceRect={{ x: 0, y: 0, width: source.width, height: source.height }}
          outputSize={{ width: source.width, height: source.height }}
          className="absolute inset-0 size-full object-cover"
          ariaLabel="Story media preview"
        />
        <div
          className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/75 via-black/35 to-transparent px-4 pt-3 text-white"
          style={{ height: `${STORY_TOP_ZONE.rect.height * 100}%` }}
          data-testid={TEST_IDS.previewStoryTopChrome}
        >
          <div className="flex gap-1">
            <span className="h-0.5 flex-1 rounded-full bg-white" />
            <span className="h-0.5 flex-1 rounded-full bg-white/45" />
            <span className="h-0.5 flex-1 rounded-full bg-white/45" />
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold">
            <span className="size-8 rounded-full border border-white/70 bg-white/25" />
            <span>your_name</span>
            <span className="font-normal text-white/70">now</span>
          </div>
        </div>
        <div
          className="absolute inset-x-0 bottom-0 flex items-end gap-3 bg-gradient-to-t from-black/75 via-black/30 to-transparent px-4 pb-5 text-white"
          style={{ height: `${STORY_BOTTOM_ZONE.rect.height * 100}%` }}
          data-testid={TEST_IDS.previewStoryBottomChrome}
        >
          <div className="flex h-10 flex-1 items-center rounded-full border border-white/80 px-4 text-xs">Reply to your_name…</div>
          <Heart className="mb-2 size-6" aria-label="Like story" />
          <Send className="mb-2 size-6" aria-label="Share story" />
        </div>
      </div>
    </div>
  )
}
